import { ref, computed } from 'vue';
import { ServiceLocator } from '@/services/ServiceLocator';
import { currentChat } from './useChatManager';

export function useShareChat() {
  const sharedLinkService = ServiceLocator.getSharedLinkService();

  const sharedLink = ref<string | null>(null); // raw id from backend
  const shareLink = ref<string>(''); // full link for UI
  const loading = ref(false);
  const error = ref<string | null>(null);

  const chatId = computed(() => {
    const id = currentChat.value?.id;
    return id && id !== 'new' ? id : null;
  });

  const createSharedLink = async () => {
    if (!chatId.value) {
      error.value = 'Please send a message before sharing this chat.';
      return;
    }

    loading.value = true;
    error.value = null;

    try {
      // Step 1: Create link on backend
      await sharedLinkService.createSharedLink(chatId.value);

      // Step 2: Confirm it's available by fetching it again
      const confirmedId = await sharedLinkService.getSharedLink(chatId.value);

      if (confirmedId) {
        sharedLink.value = confirmedId;
        shareLink.value = `${window.location.origin}?sharedId=${confirmedId}`;
      } else {
        error.value = 'Link not ready yet. Try again in a few seconds.';
      }
    } catch (err: unknown) {
      const typedErr = err as { response?: { status?: number } };
      if (typedErr?.response?.status === 409) {
        error.value =
          'A shared link already exists. Please delete it before creating a new one.';
      } else {
        error.value = 'Failed to create shared link';
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteSharedLink = async () => {
    if (!chatId.value || !sharedLink.value) return;

    loading.value = true;
    error.value = null;

    try {
      await sharedLinkService.deleteSharedLink(chatId.value, sharedLink.value);
      sharedLink.value = null;
      shareLink.value = '';
    } catch {
      error.value = 'Failed to delete shared link';
    } finally {
      loading.value = false;
    }
  };

  const copyShareLink = async () => {
    if (!shareLink.value) return;
    try {
      await navigator.clipboard.writeText(shareLink.value);
    } catch {
      return;
    }
  };

  const loadSharedLink = async () => {
    if (!chatId.value) return;

    loading.value = true;
    error.value = null;

    try {
      const existingId = await sharedLinkService.getSharedLink(chatId.value);
      if (existingId) {
        sharedLink.value = existingId;
        shareLink.value = `${window.location.origin}?sharedId=${sharedLink.value}`;
      } else {
        sharedLink.value = null;
        shareLink.value = '';
      }
    } catch {
      error.value = 'Failed to load shared link';
    } finally {
      loading.value = false;
    }
  };

  return {
    sharedLink,
    shareLink,
    createSharedLink,
    deleteSharedLink,
    copyShareLink,
    loadSharedLink,
    loading,
    error,
  };
}
